import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { requestSelectors } from 'src/store/requests/selectors';
import { getFloorItemType, getView2dSelectedTypes } from 'src/store/view-2d/selectors';
import { EFloorType } from 'src/store/view-2d/reducer';
import { ListOfKeysFilterAllData } from 'src/typings/filter';
import { Viewport } from 'src/components/Viewport';
import { useFillFloorsItem } from 'src/components/CrosstableContent/components/Section/hooks';
import { UseNumberFormat } from 'src/hooks';
import { View2dType } from 'src/typings/view-2d';
import { useStyles } from './styles';
import { ISection, View2dTypeMappingToPlacementType } from './types';
import { useSectionGenerator } from './hooks';
import { FloorItem } from './FloorItem';
import { FloorItemBig } from './components/FloorItemBig';
import { FloorItemBigSkeleton } from './components/FloorItemBig/skeleton';

export const Section: React.FC<ISection> = React.memo(({
  floorList,
  sectionName,
  sectionData,
}) => {
  const floorItemType = useSelector(getFloorItemType);
  const mainColor = useSelector(requestSelectors.app.getMainColors);
  const { fillFloors } = useSectionGenerator(floorList, sectionData);
  const view2dSelectedType = useSelector(getView2dSelectedTypes);
  const { getFillFloorsItemText } = useFillFloorsItem();
  const { numberSeparator } = UseNumberFormat();

  const fontSize = useMemo(() => {
    const result: number[] = [];
    floorList.forEach((floor) => {
      // eslint-disable-next-line no-unused-expressions
      fillFloors?.[floor] && fillFloors[floor].forEach((fillFloorsItem) => {
        if (fillFloorsItem) {
          const value = getFillFloorsItemText(fillFloorsItem);
          if (value) result.push(value?.length >= 4 ? 10 : 17);
        }
      });
    });
    return result.length ? Math.min(...result) : 10;
  }, [floorList, fillFloors, view2dSelectedType]);

  const styles = useStyles({
    ...mainColor,
    isBigFloorItem: floorItemType === EFloorType.moreInfo,
    fontSize,
  });

  const getView2dTypeMappingToPlacementType = (value: View2dType): ListOfKeysFilterAllData => {
    return View2dTypeMappingToPlacementType[value];
  };

  return (
    <div>
      <div className={styles.sectionTitle}>
        <span>{sectionName}</span>
      </div>
      <div className={styles.sectionContent}>
        {floorList.map((floor) => {
          return (
            <div key={floor} className={styles.sectionContentRow}>
              {fillFloors && fillFloors[floor] && (
                <>
                  {
                    fillFloors[floor].length
                      // eslint-disable-next-line no-nested-ternary
                      ? fillFloors[floor].map((fillFloorsItem, index) => (fillFloorsItem ? (
                        floorItemType === EFloorType.mini ? (
                          <FloorItem
                            key={`${fillFloorsItem[ListOfKeysFilterAllData.id]}${fillFloorsItem[ListOfKeysFilterAllData.placementStatus]}`}
                            fillFloorsItem={fillFloorsItem}
                            fontSize={fontSize}
                            floorItemText={numberSeparator(getFillFloorsItemText(fillFloorsItem), getView2dTypeMappingToPlacementType(view2dSelectedType))}
                          />
                        ) : (
                          <Viewport
                            key={`${fillFloorsItem[ListOfKeysFilterAllData.id]}${fillFloorsItem[ListOfKeysFilterAllData.placementStatus]}`}
                            // eslint-disable-next-line react/no-unused-prop-types
                            render={({ visible }: { visible: boolean}) => {
                              if (!visible) {
                                return (
                                  <FloorItemBigSkeleton fillFloorsItem={fillFloorsItem} />
                                );
                              }
                              return (
                                <FloorItemBig fillFloorsItem={fillFloorsItem} />
                              );
                            }}
                          />

                        )
                      ) : (
                        <div
                        // eslint-disable-next-line react/no-array-index-key
                          key={sectionName + floor + index}
                          className={floorItemType === EFloorType.mini ? 'sectionContentWrapper' : styles.sectionContentWrapperBig}
                        />
                      )))
                      : (
                        <div
                          className={styles.emptyItem}
                          data-floors={floor}
                        />
                      )
                  }
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});
